import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import WorkshopCard from './components/WorkshopCard';
import FilterPanel from './components/FilterPanel';
import WorkshopDetailModal from './components/WorkshopDetailModal';
import SearchBar from './components/SearchBar';
import SaveToast from '../portfolio-builder/components/SaveToast';

const WorkshopCatalog = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    category: 'all',
    difficulty: 'all',
    sort: 'newest',
    startDate: '',
    endDate: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [enrolledWorkshops, setEnrolledWorkshops] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toastState, setToastState] = useState({ visible: false, message: '', variant: 'success' });

  // Fetch workshops from Supabase
  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('workshops')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Transform data to match expected format
        const transformedWorkshops = (data || []).map(workshop => ({
          id: workshop.id,
          title: workshop.title,
          description: workshop.description,
          fullDescription: workshop.description,
          image: workshop.images && workshop.images.length > 0 ? workshop.images[0] : 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655',
          imageAlt: workshop.title,
          instructor: workshop.instructor || 'TBA',
          instructorTitle: workshop.instructor_title || '',
          organizerImage: workshop.instructor_image || '',
          organizerImageAlt: workshop.instructor_image_alt || '',
          duration: workshop.duration || '',
          participants: workshop.participants || 0,
          spotsLeft: workshop.spots_left ?? 0,
          nextSession: workshop.start_date,
          difficulty: workshop.difficulty || '',
          category: workshop.category || 'general',
          verified: workshop.verified || false,
          isNew: workshop.is_new || false,
          enrolled: false,
          rating: workshop.rating || 0,
          prerequisites: workshop.prerequisites || [],
          learningOutcomes: workshop.learning_outcomes || [],
          skills: workshop.skills || [],
          format: workshop.format,
          platform: workshop.platform,
          address: workshop.address || '',
          start_date: workshop.start_date,
          end_date: workshop.end_date,
          upcomingSessions: [
            { date: workshop.start_date, spotsLeft: workshop.spots_left ?? 0 }
          ],
          reviews: []
        }));

        setWorkshops(transformedWorkshops);
        console.log('Fetched workshops:', transformedWorkshops);

        // Fetch user's enrollments if logged in
        if (user) {
          const { data: enrollmentsData, error: enrollmentsError } = await supabase
            .from('workshop_enrollments')
            .select('workshop_id')
            .eq('user_id', user.id);

          if (!enrollmentsError && enrollmentsData) {
            const enrolledIds = enrollmentsData.map(e => e.workshop_id);
            setEnrolledWorkshops(enrolledIds);
          }
        }
      } catch (error) {
        console.error('Error fetching workshops:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshops();
  }, [user]);

  // Old hardcoded workshops array removed
  const oldWorkshops = [
  {
    id: 1,
    title: "Crafting Compelling Personal Statements",
    description: "Learn to write personal statements that capture admissions officers\' attention and showcase your unique story.",
    fullDescription: `This comprehensive workshop guides you through the entire personal statement writing process, from brainstorming to final edits. You'll learn proven techniques for storytelling, structure, and authenticity that make your application stand out.\n\nOur expert instructors will help you identify your unique narrative, develop compelling opening hooks, and craft conclusions that leave lasting impressions. Through interactive exercises and peer feedback sessions, you'll refine your writing skills and gain confidence in presenting your authentic self.`,
    image: "https://images.unsplash.com/photo-1699075092783-8e58982bc3d8",
    imageAlt: "Young woman with glasses writing in notebook at wooden desk with laptop and coffee cup in bright modern study space",
    instructor: "Dr. Sarah Mitchell",
    instructorTitle: "Former Harvard Admissions Officer",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1b43e8b7f-1763295504724.png",
    instructorImageAlt: "Professional headshot of Caucasian woman with shoulder-length brown hair wearing navy blazer and white blouse",
    duration: "4 weeks",
    participants: 156,
    nextSession: "2025-12-10T18:00:00",
    difficulty: "Beginner",
    category: "personal-statements",
    verified: true,
    isNew: true,
    enrolled: false,
    rating: 4.8,
    prerequisites: [],
    learningOutcomes: [
    "Develop a compelling narrative structure for your personal statement",
    "Master techniques for authentic self-presentation",
    "Learn to write engaging opening paragraphs",
    "Understand what admissions officers look for",
    "Receive personalized feedback on your drafts"],

    upcomingSessions: [
    { date: "2025-12-10T18:00:00", spotsLeft: 12 },
    { date: "2025-12-17T18:00:00", spotsLeft: 15 },
    { date: "2026-01-07T18:00:00", spotsLeft: 20 }],

    reviews: [
    {
      id: 1,
      studentName: "Emily Chen",
      studentImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6e34819-1763295702221.png",
      studentImageAlt: "Professional headshot of Asian woman with long black hair wearing light blue shirt",
      rating: 5.0,
      date: "2025-11-15",
      comment: "Dr. Mitchell\'s guidance was invaluable. She helped me find my authentic voice and structure my story in a way that truly represented who I am. Got accepted to my dream school!"
    },
    {
      id: 2,
      studentName: "Marcus Johnson",
      studentImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1faa738ad-1763294932464.png",
      studentImageAlt: "Professional headshot of African American man with short hair wearing dark suit and tie",
      rating: 4.5,
      date: "2025-10-28",
      comment: "The workshop exceeded my expectations. The peer feedback sessions were incredibly helpful, and Dr. Mitchell's expertise shone through every lesson."
    }]

  },
  {
    id: 2,
    title: "Mock Interview Mastery",
    description: "Practice university interviews with experienced admissions professionals and receive detailed feedback.",
    fullDescription: `Prepare for university interviews with confidence through our intensive mock interview program. This workshop simulates real admissions interviews, providing you with practical experience and expert feedback.\n\nYou'll participate in multiple mock interviews covering common and challenging questions, learn body language techniques, and develop strategies for articulating your thoughts clearly under pressure. Our instructors are former admissions officers who know exactly what universities are looking for.`,
    image: "https://images.unsplash.com/photo-1637621982007-44aaf9f9b0c6",
    imageAlt: "Two professionals conducting interview in modern office with glass walls and natural lighting",
    instructor: "Prof. James Anderson",
    instructorTitle: "Oxford Interview Coach",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_129cc3c7b-1763293178950.png",
    instructorImageAlt: "Professional headshot of Caucasian man with gray hair and beard wearing charcoal suit",
    duration: "3 weeks",
    participants: 203,
    nextSession: "2025-12-08T16:00:00",
    difficulty: "Intermediate",
    category: "interview-prep",
    verified: true,
    isNew: false,
    enrolled: false,
    rating: 4.9,
    prerequisites: ["Completed application draft", "Basic interview preparation"],
    learningOutcomes: [
    "Master common interview question responses",
    "Develop confident body language and presentation",
    "Learn to handle unexpected questions gracefully",
    "Practice articulating your academic interests",
    "Receive personalized feedback from experts"],

    upcomingSessions: [
    { date: "2025-12-08T16:00:00", spotsLeft: 8 },
    { date: "2025-12-15T16:00:00", spotsLeft: 10 },
    { date: "2026-01-05T16:00:00", spotsLeft: 15 }],

    reviews: [
    {
      id: 1,
      studentName: "Sophia Rodriguez",
      studentImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef203140-1763295320617.png",
      studentImageAlt: "Professional headshot of Hispanic woman with curly brown hair wearing burgundy blouse",
      rating: 5.0,
      date: "2025-11-20",
      comment: "Prof. Anderson\'s mock interviews were incredibly realistic. The feedback I received helped me feel prepared and confident for my actual interviews."
    }]

  },
  {
    id: 3,
    title: "Application Strategy & Timeline Planning",
    description: "Develop a comprehensive application strategy and timeline to maximize your chances of admission.",
    fullDescription: `This strategic workshop helps you create a personalized application roadmap tailored to your goals and circumstances. Learn how to balance reach, target, and safety schools while managing multiple deadlines.\n\nWe'll cover application components, testing strategies, recommendation letter management, and how to present a cohesive narrative across all materials. You'll leave with a detailed timeline and action plan for your entire application season.`,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b860a495-1764653521084.png",
    imageAlt: "Organized desk with calendar planner, laptop, sticky notes, and coffee cup showing strategic planning setup",
    instructor: "Maria Gonzalez",
    instructorTitle: "College Counseling Director",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1e585f295-1763295781616.png",
    instructorImageAlt: "Professional headshot of Hispanic woman with straight black hair wearing teal blazer",
    duration: "2 weeks",
    participants: 178,
    nextSession: "2025-12-12T19:00:00",
    difficulty: "Beginner",
    category: "application-strategy",
    verified: true,
    isNew: false,
    enrolled: false,
    rating: 4.7,
    prerequisites: [],
    learningOutcomes: [
    "Create a personalized application timeline",
    "Learn to balance school selection strategically",
    "Understand application components and requirements",
    "Develop a cohesive application narrative",
    "Master deadline and document management"],

    upcomingSessions: [
    { date: "2025-12-12T19:00:00", spotsLeft: 18 },
    { date: "2025-12-19T19:00:00", spotsLeft: 20 },
    { date: "2026-01-09T19:00:00", spotsLeft: 25 }],

    reviews: [
    {
      id: 1,
      studentName: "David Kim",
      studentImage: "https://img.rocket.new/generatedImages/rocket_gen_img_13a48293d-1763296098326.png",
      studentImageAlt: "Professional headshot of Asian man with short black hair wearing gray suit",
      rating: 4.5,
      date: "2025-11-10",
      comment: "Maria's systematic approach helped me stay organized throughout the application process. The timeline template was a lifesaver!"
    }]

  },
  {
    id: 4,
    title: "Career Exploration for University Selection", description: "Discover career paths and align your university choices with your professional goals.",
    fullDescription: `This exploratory workshop helps you connect your interests and strengths to potential career paths, informing smarter university and program selection decisions.\n\nThrough assessments, industry insights, and alumni conversations, you'll gain clarity on career options and understand how different universities and programs can support your professional aspirations. We'll also cover emerging fields and future job market trends.`,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1478bcc45-1764651525877.png", imageAlt: "Business professionals collaborating around conference table with laptops and documents in bright modern office", instructor: "Dr. Robert Chen", instructorTitle: "Career Development Specialist", instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1e1e7378a-1763295781630.png", instructorImageAlt: "Professional headshot of Asian man with glasses wearing navy suit and striped tie", duration: "3 weeks", participants: 142, nextSession: "2025-12-14T17:00:00",
    difficulty: "Beginner", category: "career-exploration",
    verified: true,
    isNew: true,
    enrolled: false,
    rating: 4.6,
    prerequisites: [],
    learningOutcomes: [
    "Identify career paths aligned with your interests", "Understand how university programs support career goals", "Learn about emerging industries and opportunities", "Connect with professionals in fields of interest", "Make informed decisions about major selection"],

    upcomingSessions: [
    { date: "2025-12-14T17:00:00", spotsLeft: 14 },
    { date: "2025-12-21T17:00:00", spotsLeft: 16 },
    { date: "2026-01-11T17:00:00", spotsLeft: 20 }],

    reviews: [
    {
      id: 1,
      studentName: "Jessica Taylor", studentImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6143575-1763294522920.png", studentImageAlt: "Professional headshot of Caucasian woman with blonde hair wearing white blouse", rating: 5.0, date: "2025-11-18", comment: "Dr. Chen helped me discover career paths I hadn\'t considered. This workshop completely changed my approach to university selection."
    }]

  },
  {
    id: 5,
    title: "Advanced Essay Writing Techniques",
    description: "Master advanced writing strategies for supplemental essays and scholarship applications.",
    fullDescription: `Take your essay writing to the next level with advanced techniques for crafting memorable supplemental essays and scholarship applications. This workshop is designed for students who have mastered the basics and want to refine their skills.\n\nLearn sophisticated storytelling methods, how to address challenging prompts, and techniques for writing under word count constraints. We'll analyze successful essays and practice writing compelling responses to various prompt types.`,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e99c5a48-1764735548770.png",
    imageAlt: "Student writing in notebook with laptop and books on wooden desk in cozy study environment",
    instructor: "Prof. Elizabeth Wright",
    instructorTitle: "Creative Writing Professor",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_17784c577-1763297418164.png",
    instructorImageAlt: "Professional headshot of Caucasian woman with red hair wearing emerald green blazer",
    duration: "4 weeks",
    participants: 98,
    nextSession: "2025-12-16T18:30:00",
    difficulty: "Advanced",
    category: "personal-statements",
    verified: true,
    isNew: false,
    enrolled: false,
    rating: 4.9,
    prerequisites: ["Completed basic personal statement workshop", "Draft personal statement"],
    learningOutcomes: [
    "Master advanced storytelling techniques",
    "Learn to write compelling supplemental essays",
    "Develop strategies for word count constraints",
    "Understand how to address challenging prompts",
    "Refine your unique writing voice"],

    upcomingSessions: [
    { date: "2025-12-16T18:30:00", spotsLeft: 6 },
    { date: "2026-01-06T18:30:00", spotsLeft: 8 },
    { date: "2026-01-20T18:30:00", spotsLeft: 10 }],

    reviews: [
    {
      id: 1,
      studentName: "Alexander Brown",
      studentImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1d05c3a70-1763292600546.png",
      studentImageAlt: "Professional headshot of Caucasian man with brown hair wearing black suit",
      rating: 5.0,
      date: "2025-11-05",
      comment: "Prof. Wright\'s advanced techniques transformed my essays. The feedback was detailed and actionable. Highly recommend for serious applicants."
    }]

  },
  {
    id: 6,
    title: "Financial Aid & Scholarship Applications",
    description: "Navigate financial aid processes and maximize scholarship opportunities for university funding.",
    fullDescription: `Understand the complex world of financial aid and scholarship applications with this comprehensive workshop. Learn how to identify opportunities, complete applications effectively, and maximize your funding potential.\n\nWe'll cover FAFSA completion, CSS Profile, merit scholarships, need-based aid, and strategies for writing winning scholarship essays. You'll also learn how to compare financial aid packages and make informed decisions about university affordability.`,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_184ad348a-1764680741648.png",
    imageAlt: "Calculator with financial documents, pen, and dollar bills on desk representing financial planning",
    instructor: "Linda Martinez",
    instructorTitle: "Financial Aid Advisor",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1218ba16e-1763295097494.png",
    instructorImageAlt: "Professional headshot of Hispanic woman with brown hair wearing professional gray suit",
    duration: "3 weeks",
    participants: 215,
    nextSession: "2025-12-11T19:30:00",
    difficulty: "Intermediate",
    category: "application-strategy",
    verified: true,
    isNew: false,
    enrolled: false,
    rating: 4.8,
    prerequisites: ["Basic understanding of university costs"],
    learningOutcomes: [
    "Complete FAFSA and CSS Profile accurately",
    "Identify scholarship opportunities",
    "Write compelling scholarship essays",
    "Compare financial aid packages effectively",
    "Understand different types of financial aid"],

    upcomingSessions: [
    { date: "2025-12-11T19:30:00", spotsLeft: 10 },
    { date: "2025-12-18T19:30:00", spotsLeft: 12 },
    { date: "2026-01-08T19:30:00", spotsLeft: 15 }],

    reviews: [
    {
      id: 1,
      studentName: "Michael Thompson",
      studentImage: "https://img.rocket.new/generatedImages/rocket_gen_img_17b502726-1763293415212.png",
      studentImageAlt: "Professional headshot of African American man with short hair wearing navy blazer",
      rating: 4.5,
      date: "2025-11-12",
      comment: "Linda\'s expertise in financial aid was evident throughout. She helped me find scholarships I didn\'t know existed and guided me through the application process."
    }]

  }];
  // End of old hardcoded workshops - kept for reference only, not used


  const searchSuggestions = [
  "Personal statement writing",
  "Interview preparation",
  "Application strategy",
  "Career exploration",
  "Essay writing",
  "Financial aid",
  "Scholarship applications",
  "University selection",
  "Mock interviews",
  "Supplemental essays"];


  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      category: 'all',
      difficulty: 'all',
      sort: 'newest',
      startDate: '',
      endDate: ''
    });
    setSearchTerm('');
  };

  const handleEnroll = async (workshop, session = null) => {
    if (!user) {
      setToastState({ visible: true, message: 'Please log in to enroll in workshops', variant: 'error' });
      return;
    }

    if (enrolledWorkshops?.includes(workshop?.id)) {
      setToastState({ visible: true, message: 'You are already enrolled in this workshop', variant: 'error' });
      return;
    }

    try {
      const { error } = await supabase
        .from('workshop_enrollments')
        .insert([
          {
            user_id: user.id,
            workshop_id: workshop.id
          }
        ]);

      if (error) throw error;

      setEnrolledWorkshops((prev) => [...prev, workshop?.id]);
      setToastState({ 
        visible: true, 
        message: `Successfully enrolled in "${workshop?.title}"`,
        variant: 'success' 
      });
    } catch (error) {
      console.error('Error enrolling in workshop:', error);
      setToastState({ visible: true, message: 'Failed to enroll in workshop. Please try again.', variant: 'error' });
    }
  };

  const handleViewDetails = (workshop) => {
    setSelectedWorkshop(workshop);
  };

  const getFilteredWorkshops = () => {
    let filtered = [...workshops];

    if (searchTerm) {
      filtered = filtered?.filter((workshop) =>
      workshop?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      workshop?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      workshop?.instructor?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (filters?.category !== 'all') {
      filtered = filtered?.filter((workshop) => 
        workshop?.category?.toLowerCase() === filters?.category?.toLowerCase()
      );
    }

    if (filters?.difficulty !== 'all') {
      filtered = filtered?.filter((workshop) => workshop?.difficulty === filters?.difficulty);
    }

    if (filters?.startDate) {
      filtered = filtered?.filter((workshop) =>
      new Date(workshop.nextSession) >= new Date(filters.startDate)
      );
    }

    if (filters?.endDate) {
      filtered = filtered?.filter((workshop) =>
      new Date(workshop.nextSession) <= new Date(filters.endDate)
      );
    }

    switch (filters?.sort) {
      case 'popular':
        filtered?.sort((a, b) => b?.participants - a?.participants);
        break;
      case 'upcoming':
        filtered?.sort((a, b) => new Date(a.nextSession) - new Date(b.nextSession));
        break;
      case 'rating':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'newest':
      default:
        filtered?.sort((a, b) => b?.id - a?.id);
    }

    return filtered?.map((workshop) => ({
      ...workshop,
      enrolled: enrolledWorkshops?.includes(workshop?.id)
    }));
  };

  const filteredWorkshops = getFilteredWorkshops();

  return (
    <>
      <Helmet>
        <title>Workshop Catalog - UniSteps</title>
        <meta
          name="description"
          content="Explore UniSteps workshops led by admissions experts to strengthen your applications, interviews, and overall strategy."
        />
      </Helmet>
      <div className="min-h-screen app-shell">
        <Header />

        <main className="pt-24 pb-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            <div className="surface-card rounded-2xl p-6 lg:p-8 overflow-hidden relative lifted">
              <div className="absolute inset-0 tile-grid opacity-60" aria-hidden="true" />
              <div className="absolute inset-0 aurora-blob opacity-45" aria-hidden="true" />
              <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-2">
                  <p className="text-sm uppercase tracking-[0.28em] text-muted-foreground">Expert-led sprints</p>
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground headline-serif">Workshops tailored to elevate your application</h1>
                  <p className="text-muted-foreground max-w-3xl">Filter by focus, difficulty, and timing—every session blends academic rigor with practical outcomes.</p>
                </div>
                <div className="glass-panel glass-stroke rounded-xl px-4 py-3 w-full md:w-64">
                  <p className="text-sm text-muted-foreground">Enrolled</p>
                  <p className="text-lg font-semibold text-foreground">{enrolledWorkshops?.length || 0} active</p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <SearchBar
                onSearch={setSearchTerm}
                suggestions={searchSuggestions} />

            </div>

            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-muted-foreground">
                Showing {filteredWorkshops?.length} workshop{filteredWorkshops?.length !== 1 ? 's' : ''}
              </p>
              <Button
                variant="outline"
                size="sm"
                iconName="SlidersHorizontal"
                iconPosition="left"
                onClick={() => setIsFilterOpen(true)}
                className="lg:hidden">

                Filters
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleResetFilters}
                  isOpen={isFilterOpen}
                  onClose={() => setIsFilterOpen(false)} />

              </div>

              <div className="lg:col-span-3">
                {filteredWorkshops?.length > 0 ?
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredWorkshops?.map((workshop) =>
                  <WorkshopCard
                    key={workshop?.id}
                    workshop={workshop}
                    onEnroll={handleEnroll}
                    onViewDetails={handleViewDetails} />

                  )}
                  </div> :

                <div className="text-center py-16">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No workshops found
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Try adjusting your filters or search terms
                    </p>
                    <Button
                    variant="outline"
                    iconName="RotateCcw"
                    iconPosition="left"
                    onClick={handleResetFilters}>

                      Clear Filters
                    </Button>
                  </div>
                }
              </div>
            </div>
          </div>
        </main>

        {selectedWorkshop &&
        <WorkshopDetailModal
          workshop={selectedWorkshop}
          onClose={() => setSelectedWorkshop(null)}
          onEnroll={handleEnroll} />

        }

        <SaveToast
          message={toastState.message}
          variant={toastState.variant}
          visible={toastState.visible}
          onClose={() => setToastState({ visible: false, message: '', variant: 'success' })}
        />
      </div>
    </>);

};

export default WorkshopCatalog;